// Localização: frontend/src/hooks/usePlanAccess.js
// Hook para verificar acesso a módulos baseado no plano de assinatura

import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    isModuleAvailable,
    getRequiredPlanInfo,
    getRequiredPlanForModule,
    hasPlanAccess
} from '../services/planService';

/**
 * Hook para verificar acesso a módulos baseado no plano de assinatura
 * @returns {object} Objeto com funções de verificação de acesso
 */
export const usePlanAccess = () => {
    const { subscription, hasActiveSubscription, user } = useAuth();

    // Obter ID do plano atual
    const currentPlanoId = useMemo(() => {
        // ✅ CORREÇÃO: Considerar trial Premium mesmo sem organização
        if (!subscription || !hasActiveSubscription()) {
            return null;
        }
        const planoId = subscription.plano_id ? parseInt(subscription.plano_id, 10) : null;
        return planoId;
    }, [subscription, hasActiveSubscription, user?.organizacao_id]);

    /**
     * Verifica se um módulo está disponível no plano atual
     * @param {string} moduleKey - Chave do módulo
     * @returns {boolean} true se o módulo está disponível
     */
    const isModuleAvailableInPlan = (moduleKey) => {
        // ✅ CORREÇÃO: Se tem trial ativo, verificar acesso mesmo sem currentPlanoId calculado
        const planoId = subscription?.plano_id ? parseInt(subscription.plano_id, 10) : null;
        if (subscription?.is_trial && subscription?.status === 'TRIAL' && hasActiveSubscription() && planoId) {
            return isModuleAvailable(planoId, moduleKey);
        }
        
        // Se não tem assinatura ativa, nenhum módulo está disponível
        if (!currentPlanoId || !hasActiveSubscription()) {
            return false;
        }
        return isModuleAvailable(currentPlanoId, moduleKey);
    };

    /**
     * Retorna informações do plano necessário para acessar um módulo
     * @param {string} moduleKey - Chave do módulo
     * @returns {object|null} Objeto com { id, nome } ou null
     */
    const getRequiredPlan = (moduleKey) => {
        return getRequiredPlanInfo(moduleKey);
    };

    /**
     * Verifica se o usuário tem assinatura ativa
     * @returns {boolean} true se tem assinatura ativa
     */
    const hasActiveSubscriptionInContext = () => {
        return subscription && hasActiveSubscription();
    };

    /**
     * Verifica se o usuário precisa fazer upgrade para acessar um módulo
     * @param {string} moduleKey - Chave do módulo
     * @returns {boolean} true se precisa fazer upgrade
     */
    const needsUpgrade = (moduleKey) => {
        // ✅ CORREÇÃO: Verificar se é trial (qualquer plano, não só Premium)
        const planoId = subscription?.plano_id ? parseInt(subscription.plano_id, 10) : null;
        const isTrial = subscription?.is_trial;
        const isTrialActive = isTrial && hasActiveSubscription();
        
        // Se é trial ativo, verificar acesso ao módulo
        if (isTrialActive) {
            const requiredPlanId = getRequiredPlanForModule(moduleKey);
            if (requiredPlanId && planoId) {
                const hasAccess = hasPlanAccess(planoId, requiredPlanId);
                if (hasAccess) {
                    return false; // Trial tem acesso, não precisa upgrade
                } else {
                    // Mesmo assim, não redirecionar - deixar o backend verificar
                    return false;
                }
            } else {
                // Módulo não requer plano específico, permitir acesso
                return false;
            }
        }
        
        // Se o usuário tem organizacao_id mas não tem assinatura carregada ainda,
        // permitir que a requisição vá ao backend (ele busca organizacao_id atualizado do banco)
        // Isso resolve problemas de timing quando usuário é vinculado recentemente
        if (user?.organizacao_id && !subscription) {
            return false; // Deixar o backend verificar
        }
        
        // Se não temos organizacao_id e não temos trial ativo, então precisa upgrade
        if (!user?.organizacao_id && !isTrialActive) {
            return true;
        }
        
        // ✅ CORREÇÃO: Se é trial mas não está ativo (expirado), não redirecionar aqui
        // Deixar o PrivateRoute lidar com isso
        if (isTrial && !isTrialActive) {
            return false; // Não redirecionar aqui - deixar PrivateRoute verificar
        }
        
        // Se temos subscription mas não está ativa e não é trial, então realmente precisa fazer upgrade
        if (subscription && !isTrial && (!currentPlanoId || !hasActiveSubscription())) {
            return true; // Sem assinatura ativa, precisa fazer upgrade
        }
        
        // Se não temos subscription e temos organizacao_id, pode ser que ainda não carregou
        // Mas se já tentou carregar e não tem, então realmente não tem assinatura
        if (!subscription && user?.organizacao_id) {
            // Aguardar um pouco para ver se a assinatura carrega
            // Por enquanto, deixar o backend verificar
            return false; // Deixar o backend verificar
        }
        
        const requiredPlanId = getRequiredPlanForModule(moduleKey);
        if (!requiredPlanId) {
            return false; // Módulo não requer plano específico
        }
        
        const hasAccess = hasPlanAccess(currentPlanoId, requiredPlanId);
        return !hasAccess;
    };

    /**
     * Retorna informações completas sobre acesso a um módulo
     * @param {string} moduleKey - Chave do módulo
     * @returns {object} Objeto com informações de acesso
     */
    const getModuleAccessInfo = (moduleKey) => {
        const available = isModuleAvailableInPlan(moduleKey);
        const requiredPlan = getRequiredPlan(moduleKey);
        const upgradeNeeded = needsUpgrade(moduleKey);
        const currentPlan = subscription ? {
            id: subscription.plano_id,
            nome: subscription.plano_nome || 'Desconhecido'
        } : null;

        return {
            available,
            upgradeNeeded,
            currentPlan,
            requiredPlan,
            hasActiveSubscription: hasActiveSubscription()
        };
    };

    return {
        currentPlanoId,
        isModuleAvailable: isModuleAvailableInPlan,
        getRequiredPlan,
        needsUpgrade,
        getModuleAccessInfo,
        hasActiveSubscription: hasActiveSubscription()
    };
};

